import { INTERRUPTED_ERROR } from '@crowdex/agent-contracts';
import { ToolContent, ToolHeader, ToolInput, ToolOutput } from '@crowdex/ui/ai/tool';
import { Button } from '@crowdex/ui/components/button';
import { useContext } from 'react';
import { InterruptedActionContext } from '@/lib/conversations/message-part/interrupted-action-context';
import {
  isBackgroundTerminal,
  terminalExitLabel,
  terminalRunning,
} from '@/lib/conversations/message-part/terminal';
import {
  browserToolTitle,
  codeToolTitle,
  dynamicToolAction,
  toolDetail,
} from '@/lib/conversations/message-part/tool-identity';
import type { DynamicToolPart, TerminalPart } from '@/lib/conversations/message-part/types';
import { visibleToolErrorText } from '@/lib/conversations/message-parts';
import { BackgroundTerminalCard } from './background-terminal-card';
import { StableTool } from './stable-tool';

export function DynamicTool({
  onOpenTerminal,
  part,
  terminal,
}: {
  onOpenTerminal?: (terminalId: string) => void;
  part: DynamicToolPart;
  terminal?: TerminalPart;
}) {
  const errorText = part.state === 'output-error' ? part.errorText : undefined;
  const interrupted = errorText === INTERRUPTED_ERROR;
  const visibleErrorText = interrupted
    ? undefined
    : visibleToolErrorText(errorText, terminal !== undefined);
  const resume = useContext(InterruptedActionContext);
  const canResume = interrupted && resume !== null && resume.activeToolCallId === part.toolCallId;
  // A code-mode step says what its code calls ("List avatars"): that is its action.
  const codeTitle = codeToolTitle(part);
  const readableTitle =
    codeTitle === null ? browserToolTitle(part.toolName, part.title, part.input) : null;
  const action = codeTitle ?? dynamicToolAction(part);
  const detail = codeTitle ?? toolDetail(part, readableTitle);
  return (
    <StableTool className="py-0.5 text-muted-foreground">
      <ToolHeader
        action={action}
        interrupted={interrupted}
        state={part.state}
        title={detail}
        type={`tool-${codeTitle !== null ? 'code' : readableTitle === null ? part.toolName : 'browser'}`}
      />
      <ToolContent>
        <ToolInput input={part.input ?? null} />
        <ToolOutput
          errorText={visibleErrorText}
          output={
            terminal !== undefined && isBackgroundTerminal(terminal.data.output) ? (
              <BackgroundTerminalCard
                command={detail}
                data={terminal.data}
                onOpen={
                  onOpenTerminal === undefined
                    ? undefined
                    : () => onOpenTerminal(terminal.data.terminalId)
                }
              />
            ) : terminal !== undefined ? (
              <div className="overflow-hidden bg-desktop-terminal text-desktop-terminal-foreground">
                <div className="flex items-center gap-2 border-hairline border-b px-3 py-1.5 text-xs">
                  <span className="font-medium">Terminal</span>
                  <span className="ml-auto text-muted-foreground">
                    {terminalExitLabel(terminal.data.exitCode, terminal.data.signal, part.state)}
                  </span>
                  {onOpenTerminal !== undefined && (
                    <button
                      className="underline underline-offset-2"
                      onClick={() => onOpenTerminal(terminal.data.terminalId)}
                      type="button"
                    >
                      Open terminal
                    </button>
                  )}
                </div>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap p-3 font-mono text-xs leading-5">
                  {terminal.data.output ||
                    (terminalRunning(terminal.data.exitCode, terminal.data.signal, part.state)
                      ? 'Waiting for output…'
                      : 'No terminal output.')}
                </pre>
                {terminal.data.truncated && (
                  <p className="border-hairline border-t px-3 py-1.5 text-muted-foreground text-xs">
                    Output truncated
                  </p>
                )}
              </div>
            ) : part.state === 'output-available' ? (
              part.output
            ) : null
          }
          showEmpty
        />
      </ToolContent>
      {canResume && resume !== null && (
        <div className="mt-1 mb-1 flex flex-wrap items-center gap-2 rounded-md border border-hairline bg-background/55 px-3 py-2">
          <span className="mr-1 text-muted-foreground text-xs">
            Action interrompue (a pu démarrer). Reprendre en vérifiant, ou ignorer.
          </span>
          <Button
            className="h-control-compact px-2.5"
            onClick={() =>
              resume.onResume({
                kind: 'tool',
                toolCallId: part.toolCallId,
                toolName: part.toolName,
                ...(part.title === undefined ? {} : { title: part.title }),
                input: part.input ?? null,
              })
            }
            size="sm"
          >
            Autoriser et exécuter
          </Button>
          <Button
            className="h-control-compact px-2.5"
            onClick={() =>
              resume.onSkip({
                kind: 'tool',
                toolCallId: part.toolCallId,
                toolName: part.toolName,
                ...(part.title === undefined ? {} : { title: part.title }),
                input: part.input ?? null,
              })
            }
            size="sm"
            variant="ghost"
          >
            Ignorer
          </Button>
        </div>
      )}
    </StableTool>
  );
}
